/**
 * Serialized in ReviewView via `saveResults()` → localStorage `courtyard_review_results`.
 * Injected into Ask / Agent / Editor model calls as fixed party-identification context.
 *
 * Entries are keyed by document identity (basename + lastSaveTime, createdAt fallback)
 * so party context from one document is never applied to another.
 */

import { getCurrentDocumentIdentity } from './reviewDocumentIdentity';
import {
  loadReviewCompleteState,
  loadReviewPartyResults,
  type ReviewResultsStored,
} from './reviewStateStorage';
import {
  REVIEW_COMPLETE_STATE_STORAGE_KEY,
  REVIEW_RESULTS_STORAGE_KEY,
} from './reviewStateStorageKeys';

export type { ReviewResultsStored };
export { REVIEW_COMPLETE_STATE_STORAGE_KEY, REVIEW_RESULTS_STORAGE_KEY };

function ourSideNonEmpty(data: ReviewResultsStored | null | undefined): boolean {
  return ((data?.ourSide ?? []) as string[]).filter(Boolean).length > 0;
}

function loadPartyForCurrentDocument(): ReviewResultsStored | null {
  const identity = getCurrentDocumentIdentity();
  if (!identity) return null;
  const party = loadReviewPartyResults(identity, REVIEW_RESULTS_STORAGE_KEY);
  if (party && ourSideNonEmpty(party)) return party;
  const complete = loadReviewCompleteState(identity, REVIEW_COMPLETE_STATE_STORAGE_KEY);
  if (complete && ourSideNonEmpty(complete)) return complete;
  return null;
}

/**
 * True when the user has finished the Review party-identification flow for the
 * currently resolved document (at least one "our side" entity saved).
 */
export function hasReviewPartyContextComplete(): boolean {
  if (typeof globalThis.window === 'undefined') return true;
  try {
    return ourSideNonEmpty(loadPartyForCurrentDocument());
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Returns a concise instruction block for the model, or undefined if the user has not completed Review sides
 * for the currently resolved document.
 */
export function getReviewPartyContextInstruction(): string | undefined {
  if (typeof globalThis.window === 'undefined') return undefined;
  try {
    const data = loadPartyForCurrentDocument();
    if (!data) return undefined;
    const our = (data.ourSide || []).filter(Boolean);
    if (our.length === 0) return undefined;
    const their = (data.theirSide || []).filter(Boolean);
    const lines: string[] = [
      'Party identification (from the Word add-in Review flow):',
      `- The user's party ("our side" / "my side"): ${our.join(', ')}`,
      `- The other side: ${their.length ? their.join(', ') : '(not specified)'}`,
    ];
    if (data.optionalDetails?.trim()) {
      lines.push(`- User notes: ${data.optionalDetails.trim()}`);
    }
    if (data.hasRedlines && data.sideAssignments && Object.keys(data.sideAssignments).length > 0) {
      const bits = Object.entries(data.sideAssignments).map(
        ([author, side]) =>
          `${author} → ${side === 'our_side' ? "the user's party" : 'the other side'}`
      );
      lines.push(`- Track changes authors mapped to sides: ${bits.join('; ')}`);
    }
    lines.push(
      'When the user refers to "my party", "our side", "favor us", or similar, use the identification above.'
    );
    return lines.join('\n');
  } catch {
    return undefined;
  }
}

/** Map a Word track-changes author to Review sides (our / their). */
export function resolveTrackChangeAuthorSide(author: string): 'our' | 'their' | null {
  const needle = (author || '').trim().toLowerCase();
  if (!needle) return null;
  try {
    const data = loadPartyForCurrentDocument();
    const assignments = data?.sideAssignments;
    if (!assignments) return null;
    for (const [name, side] of Object.entries(assignments)) {
      if (name.trim().toLowerCase() === needle) {
        return side === 'our_side' ? 'our' : 'their';
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}
